// Enums
export { Rank, Suit, RowPosition, OFCVariant, ActingMode, Label } from './enums';

// Types
export type {
  Card,
  Row,
  Entry,
  Placement,
  Board,
  GameState,
  CreateOFCHandDto,
  RoyaltyTable,
  SettlementResult,
  HandEvaluator,
  OFCVariantConfig,
  FantasyConfig,
} from './types';

// Operations
export type { Operation, HoleDealing, Placing, RoundAdvancing, Scoring, FantasyTriggering } from './operations';

// Constants
export {
  ROW_CAPACITY,
  RANK_ORDER_STANDARD,
  RANK_ORDER_DEUCE_TO_SEVEN,
  OFC_VARIANT_CODES,
  STANDARD_SUITS,
} from './constants';

// Royalties
export {
  STANDARD_ROYALTIES,
  DEUCE_TO_SEVEN_ROYALTIES,
  DEUCE_TO_SEVEN_MIDDLE_ROYALTIES,
  SCOOP_BONUS,
  FOUL_PENALTY,
} from './royalties';

// Card functions
export {
  createCard,
  parseCard,
  parseCards,
  cardToString,
  cardsToString,
  getRankIndex,
  compareCards,
  cardsEqual,
  isJoker,
  sortCardsByRank,
} from './card';

// Deck functions
export { createStandardDeck, createDeckWithJokers, drawCards, shuffleDeck, shuffleDeckSeeded } from './deck';
export type { DrawResult } from './deck';

// Row functions
export {
  getRowCapacity,
  createEmptyRow,
  isRowComplete,
  getEmptySlots,
  addCardToRow,
  removeCardFromRow,
  isValidRowPlacement,
  getRowCards,
} from './row';

// Hand evaluation
export {
  compareEntries,
  getRankCounts,
  isFlush,
  isStraight,
  isWheelStraight,
  getHighCardRanks,
  buildStrengthIndex,
  evaluateHighHand,
  evaluateFrontHand,
  evaluateLowHand,
  evaluateWithJoker,
  evaluateFrontWithJoker,
} from './hands';

// Board functions
export {
  createEmptyBoard,
  getRow,
  setRow,
  getBoardTotalCards,
  isBoardComplete,
  placeCardOnBoard,
  placePlacementOnBoard,
  placePlacementsOnBoard,
  isBoardFouled,
  getFoulReason,
  getAllBoardCards,
  canPlaceOnRow,
  getAvailablePositions,
} from './board';

// Scoring functions
export {
  getFrontRoyaltyKey,
  calculateFrontRoyalty,
  calculateMiddleRoyalty,
  calculateBackRoyalty,
  calculateRowRoyalty,
  calculateBoardRoyalties,
  calculateMiddleLowRoyalty,
  compareRows,
  settleHeadToHead,
  settleMultiway,
  calculateScores,
  getPlayerFoulStatus,
} from './scoring';

// State functions
export {
  createInitialState,
  getDefaultActingMode,
  getActors,
  canAct,
  isGameComplete,
  isInitialDeal,
  isPlacementPhase,
  areAllBoardsComplete,
  getNextActor,
  isValidPlacement,
  areValidPlacements,
  updateState,
  addOperation,
  setStatus,
  setRoundIndex,
  setBoard,
  setActors,
  removeActor,
  setActorIndex,
  addDiscard,
  addDiscards,
  setFantasyPlayers,
  addFantasyPlayer,
  getBoard,
  isCardDiscarded,
  isCardOnAnyBoard,
  isCardUsed,
  isRoundComplete,
  shouldEndGame,
  getCardsDealtThisRound,
  getAvailableActions,
  getHandResults,
} from './state';
export type { AvailableActions, PlayerResult, HandResults } from './state';

// Variants (configurations)
export {
  STANDARD_OFC,
  PINEAPPLE_OFC,
  PINEAPPLE_FANTASY_OFC,
  DEUCE_TO_SEVEN_OFC,
  DEUCE_TO_SEVEN_PINEAPPLE_OFC,
  JOKER_OFC,
  JOKER_PINEAPPLE_OFC,
  getVariantConfig,
  getCardsForRound,
  getCardsToPlace,
  getCardsToDiscard,
  isPineappleVariant,
  isDeuceToSevenVariant,
  isJokerVariant,
  hasFantasy,
} from './variants';

// Games (factory functions)
export {
  StandardOFC,
  PineappleOFC,
  PineappleFantasyOFC,
  DeuceToSevenOFC,
  DeuceToSevenPineappleOFC,
  JokerOFC,
  JokerPineappleOFC,
} from './games';

// Actions
export {
  verifyPlacing,
  canPlace,
  place,
  canDealHole,
  dealHole,
  canBeginRound,
  beginRound,
  canAdvanceRound,
  advanceRound,
  canEndGame,
  endGame,
} from './actions';
export type { ActionResult, PlacingValidation } from './actions';

// Fantasy
export {
  qualifiesForFantasy,
  qualifiesViaFront,
  qualifiesViaMiddle,
  isDoubleFantasy,
  getFantasyCardCount,
  continuesFantasy,
  isPerfect27Middle,
  getFantasyTriggerInfo,
} from './fantasy';
export type { FantasyTriggerInfo } from './fantasy';

// Client Display Types
export { OFCPhase, OFCPlayerStatus } from './client-types';
export type {
  OFCAvailableActions,
  OFCCardWithState,
  OFCRowDisplay,
  OFCBoardDisplay,
  OFCPlayerDisplay,
  OFCTableState,
  OFCPlacementAction,
  OFCPendingPlacements,
  OFCScoreDisplay,
  OFCFantasyDisplay,
  OFCSeatPosition,
  OFCCardSlotDimensions,
  OFCBoardDimensions,
  CSSPositionProperties,
  RowResult,
  PlayerScoringResult,
  ScoringResults,
} from './client-types';
export {
  DEFAULT_CARD_SLOT_DESKTOP,
  DEFAULT_CARD_SLOT_MOBILE,
  calculateBoardDimensions,
  boardToDisplay,
  createEmptyBoardDisplay,
} from './client-types';
